/**
 * Copyright © 2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { type CartItem, CartPage, useCartContext } from "@farmlands/commerce";
import { useState } from "react";
import { useSelector } from "react-redux";
import { createSearchParams, useNavigate } from "react-router-dom";

import { PageContainer } from "../../../components/layout/PageContainer";
import {
  EditPriceModal,
  IEditedCartItemDetails,
} from "../../../components/modals/EditPriceModal/EditPriceModal";
import { usePermission } from "../../../hooks/usePermission";
import { useThemeBackgroundColor } from "../../../hooks/useThemeBackgroundColor";
import { selectRemoteConfig } from "../../../services/firebase/remote-config/remoteConfig.slice";
import { TestIds } from "../../../testIds";
import { selectCurrentFarmerAccount } from "../../azure-b2c/storage";
import type { CartItem as LegacyCartItem } from "../../cart/storage/cart.types";
import { EProductLocation } from "../../products/components/ProductDetails/utils/openProductDetails";
import { PRODUCTS_CART_ROUTE, PRODUCTS_ROUTES } from "../../products/constants";
import { useCartItemDisplayData } from "../hooks/useCartItemDisplayData";

export const ShopCartPage = () => {
  useThemeBackgroundColor((theme) => theme.colors.shade0);

  const currentAccount = useSelector(selectCurrentFarmerAccount);
  const { discountEnabled } = useSelector(selectRemoteConfig);
  const canEditPrice = usePermission("StaffSpecific_Pricing_PriceEdit").allowed;
  const navigate = useNavigate();
  const { items, setQuantity, setEditedPrice } = useCartContext();
  const { getDisplayItem } = useCartItemDisplayData(items);

  const [editPriceItem, setEditPriceItem] = useState<CartItem | undefined>(undefined);
  const [isEditPriceModalOpen, setIsEditPriceModalOpen] = useState(false);

  const canEditItemPrice: boolean =
    Boolean(currentAccount?.hasTfoAccess) && discountEnabled && canEditPrice;

  const handleProductClick = (item: CartItem): void => {
    const params: URLSearchParams = createSearchParams({
      size: item.size ?? "",
      colour: item.colour ?? "",
      qty: item.quantity.toString(),
    });

    navigate(
      {
        pathname: `${PRODUCTS_ROUTES.BASE}/${item.masterProductId ?? item.productId}`,
        search: params.toString(),
      },
      {
        state: {
          productLocation: EProductLocation.CART,
          previousRelativeUrl: PRODUCTS_CART_ROUTE,
        },
      }
    );
  };

  const handleEditPrice = (item: CartItem): void => {
    setEditPriceItem(item);
    setIsEditPriceModalOpen(true);
  };

  const handleApplyEditedPrice = ({
    editedPrice,
    editedPriceReasonCode,
    editedPriceReasonNote,
    editedQuantity,
    item,
  }: IEditedCartItemDetails): void => {
    if (editedQuantity !== item.quantity) {
      setQuantity(item.productId, editedQuantity);
    }

    setEditedPrice(item.productId, {
      editedPriceExcGst: editedPrice,
      lastEditedReasonCode: editedPriceReasonCode ?? null,
      lastEditedReasonNote: editedPriceReasonNote ?? null,
    });
  };

  const handleRevertEditedPrice = (item: LegacyCartItem): void => {
    setEditedPrice(item.productId, {
      editedPriceExcGst: null,
      lastEditedReasonCode: null,
      lastEditedReasonNote: null,
    });
  };

  return (
    <PageContainer data-testid={TestIds.pages.cart}>
      <CartPage
        onEditPrice={canEditItemPrice ? handleEditPrice : undefined}
        editPriceLabel="Edit price"
        onProductClick={handleProductClick}
      />

      <EditPriceModal
        open={isEditPriceModalOpen}
        setOpen={setIsEditPriceModalOpen}
        item={editPriceItem ? getDisplayItem(editPriceItem) : undefined}
        onApplyEditedPrice={handleApplyEditedPrice}
        onRevertEditedPrice={handleRevertEditedPrice}
      />
    </PageContainer>
  );
};
